<script setup>
const emit = defineEmits(["toggle"]);

import { useUserStore } from "@store/user";
import { useMetadataStore } from "@store/metadata";
import { useRoute, useRouter } from "vue-router";
// Modals moved to TopBar; keep sidebar lightweight
import UserService from "@services/user";
import { ArrowRightBold, Setting, Document } from "@element-plus/icons-vue";
import { ElNotification, ElMessageBox, ElLoading } from "element-plus";
import { useLoaderStore } from "@store/loader";
import ListsApi from "@api/lists.api";
import { usePinnedListRefresh } from "@composables/usePinnedListRefresh.js";
import ChatApi from "@api/chat.api.js";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import Logo from "../../../assets/svg/logo.svg?component";
import LogoMini from "../../../assets/svg/logoMini.svg?component";
import Collapse from "../../../assets/svg/collapse.svg?component";
import UnCollapse from "../../../assets/svg/unCollapse.svg?component";
import Home from "../../../assets/svg/home.svg?component";
import ProjectsMenu from "../../../assets/svg/projects-menu.svg?component";
import DocumentsMenu from "../../../assets/svg/documents-menu.svg?component";
import RequestsMenu from "../../../assets/svg/requests-menu.svg?component";
import MyActivitiesMenu from "../../../assets/svg/my-activities-menu.svg?component";
import AnalyticsMenu from "../../../assets/svg/analytics-menu.svg?component";
import TamersMenu from "../../../assets/svg/tamers-menu.svg?component";
import ListsMenu from "../../../assets/svg/lists-menu.svg?component";
import SettingsMenu from "../../../assets/svg/settings-menu.svg?component";
import FAQMenu from "../../../assets/svg/faq-menu.svg?component";
import UploadMenu from "../../../assets/svg/upload-menu.svg?component";
import AiIcon from "@/components/svg/ai-icon.vue";

const route = useRoute();
const router = useRouter();
const metadataStore = useMetadataStore();
let requestsCount = ref(0);
const creatingChat = ref(false);

// Pinned lists data
const myPinnedLists = ref([]);
const sharedPinnedLists = ref([]);
const pinnedListsLoading = ref(false);

const updateListsActive = () => {
    if (route.name !== "lists" && route.name !== "lists.view") return;

    const openedMenus = ["8"];
    const listTypeQuery = route.query.ListType;

    if (route.name === "lists.view") {
        const listId = route.params.id;
        const list = [...myPinnedLists.value, ...sharedPinnedLists.value].find((l) => l.id == listId);

        if (list) {
            const isMyList = ["owner", "editor"].includes(list.role);
            const subMenuIndex = isMyList ? "8-1" : "8-2";

            const filteredList = isMyList 
                ? myPinnedLists.value.slice(0, 5)
                : sharedPinnedLists.value.slice(0, 5);

            const itemIndex = filteredList.findIndex((l) => l.id == listId);

            if (itemIndex !== -1) {
                activeMenuIndex.value = `${subMenuIndex}-${itemIndex + 1}`;
                openedMenus.push(subMenuIndex);
            } else {
                activeMenuIndex.value = "";
            }
        } else {
            activeMenuIndex.value = "";
        }
    } else if (listTypeQuery) {
        const listTypes = Array.isArray(listTypeQuery)
            ? listTypeQuery
            : [listTypeQuery];
        if (listTypes.length === 1) {
            if (listTypes[0] === "my_list") {
                activeMenuIndex.value = "8-1";
                openedMenus.push("8-1");
            } else if (listTypes[0] === "shared_with_me") {
                activeMenuIndex.value = "8-2";
                openedMenus.push("8-2");
            } else {
                activeMenuIndex.value = "";
            }
        } else {
            activeMenuIndex.value = "";
        }
    } else {
        activeMenuIndex.value = "";
    }

    defaultOpenedMenus.value = openedMenus;
};

const login = () => {};

let userId = computed(() => {
    return useUserStore().user.id;
});

const businessDomains = computed(() => {
    return metadataStore.metadata.businessDomains || [];
});

const offerings = computed(() => {
    const offeringsData = metadataStore.metadata?.offering || [];
    return offeringsData.filter(
        (offering) => offering.name != "Not Applicable",
    );
});

const offeringsByBusinessDomain = computed(() => {
    const filteredOfferings = offerings.value;
    const domainsMap = new Map();

    businessDomains.value.forEach((domain) => {
        domainsMap.set(domain.id, {
            domain,
            offerings: [],
        });
    });

    filteredOfferings.forEach((offering) => {
        const domainId = offering.business_domain_id;
        if (domainId && domainsMap.has(domainId)) {
            if (offering.has_projects == true) {
                domainsMap.get(domainId).offerings.push(offering);
            }
        }
    });

    const groupedDomains = Array.from(domainsMap.values());

    groupedDomains.forEach((group) => {
        group.offerings.sort((a, b) => {
            const aCount = a.projects_count ?? 0;
            const bCount = b.projects_count ?? 0;

            if (bCount !== aCount) {
                return bCount - aCount;
            }

            return a.name.localeCompare(b.name);
        });
    });

    return groupedDomains
        .filter((item) => item.offerings.length > 0)
        .sort((a, b) => {
            const aIsOther = ["other", "others"].includes(
                a.domain.name.toLowerCase(),
            );
            const bIsOther = ["other", "others"].includes(
                b.domain.name.toLowerCase(),
            );

            if (aIsOther && !bIsOther) return 1;
            if (!aIsOther && bIsOther) return -1;

            if (b.domain.projects_count !== a.domain.projects_count) {
                return b.domain.projects_count - a.domain.projects_count;
            }

            // Equal counts: keep original backend order (stable sort)
            return 0;
        });
});

function getMyRequests(page = 1) {
    // RequestsApi.getRequests({ teamLead: userId.value })
    //     .then(({ data }) => {
    //         requestsCount.value = data.data.to_requests_pagination.total;
    //     })
    //     .catch((error) => {});
}

const navigateTo = (page) => {
    if (page === "documents") {
        sessionStorage.setItem("document_status", false);
    }
    if (route.name === page) {
        router.go(0);
    }
};

const navigateToHome = () => {
    router.push({ name: "home" });
};

const navigateToProject = () => {
    router.push({ name: "projects" });
};

const navigateToAllProjects = () => {
    router.push({ name: "projects" });
};

const navigateToProjectByBusinessDomain = (businessDomain) => {
    router.push({
        name: "projects",
        query: {
            OrderByCreatedDate: "true",
            "BusinessDomainId[0]": businessDomain.id,
        },
    });
};

const navigateToProjectByOffering = (offering) => {
    router.push({
        name: "projects",
        query: {
            OrderByCreatedDate: "true",
            "OfferingId[0]": offering.id,
            //   _t: Date.now(), // Force navigation with timestamp
        },
    });
};

const navigateToDocuments = () => {
    router.push({ name: "documents" });
};

const navigateToAllDocuments = () => {
    //router.push({ name: 'documents.category', params: { slug: 'all' } })
    window.location.href = "/documents/category/all";
};

const navigateToTamProjects = () => {
    router.push({
        name: "documents.category",
        params: { slug: "tam-projects" },
    });
};

const navigateToDomainOffering = () => {
    router.push({
        name: "documents.category",
        params: { slug: "domain-offering" },
    });
};

const navigateToSupportService = () => {
    router.push({
        name: "documents.category",
        params: { slug: "support-service" },
    });
};

const navigateToThirdParty = () => {
    router.push({
        name: "documents.category",
        params: { slug: "third-party" },
    });
};

const navigateToOtherDocuments = () => {
    router.push({ name: "documents.category", params: { slug: "other" } });
};

const navigateToRequests = (type) => {
    router.push({ name: type + "-requests" });
};

const navigateToMyActivities = () => {
    router.push({ name: "my.knowledge" });
};

const navigateToAnalytics = () => {
    router.push({ name: "analytics" });
};

const navigateToTamers = () => {
    router.push({ name: "tamers" });
};

const navigateToLists = (type) => {
    // Handle both single string and array of types
    const listTypes = Array.isArray(type) ? type : [type];
    // Vue Router handles arrays in query - it will create ?ListType=value1&ListType=value2
    router.push({
        name: "lists",
        query: {
            ListType: listTypes,
        },
    });
};

const navigateToList = (listId) => {
    router.push({
        name: "lists.view",
        params: { id: listId },
    });
};

// Fetch pinned lists
const { refreshTrigger, triggerRefresh } = usePinnedListRefresh();

const getPinnedLists = async () => {
    if (pinnedListsLoading.value) return;

    pinnedListsLoading.value = true;
    try {
        const response = await ListsApi.getPinned();
        const data = response.data.data;
        
        // Set separate arrays from API response
        myPinnedLists.value = data.myLists || [];
        sharedPinnedLists.value = data.sharedLists || [];
    } catch (error) {
        console.error("Error fetching pinned lists:", error);
        myPinnedLists.value = [];
        sharedPinnedLists.value = [];
    } finally {
        pinnedListsLoading.value = false;
    }
};

// Watch for refresh triggers
watch(refreshTrigger, () => {
    getPinnedLists();
});

const navigateToSettings = () => {
    router.push({ name: "settings" });
};

const navigateToFAQ = () => {
    router.push({ name: "faq" });
};

const navigateToUsers = () => {
    router.push({ name: "users" });
};

const navigateToRoles = () => {
    router.push({ name: "roles" });
};

const navigateToClients = () => {
    router.push({ name: "clients" });
};

const navigateToClientTypes = () => {
    router.push({ name: "clients-types" });
};

const navigateToServiceLines = () => {
    router.push({ name: "service-lines" });
};

const navigateToBusinessDomains = () => {
    router.push({ name: "business-domains" });
};

const navigateToBusinessUnits = () => {
    router.push({ name: "business-units" });
};

const navigateToOfferings = () => {
    router.push({ name: "offerings" });
};

const navigateToIndustry = () => {
    router.push({ name: "industry" });
};

const navigateToDigitalSolutions = () => {
    router.push({ name: "digital-solutions" });
};

const navigateToManagedServices = () => {
    router.push({ name: "managed-services" });
};

const navigateToDocumentTypes = () => {
    router.push({ name: "document-types" });
};

const navigateToDocumentTypesMapping = () => {
    router.push({ name: "document-types-mapping" });
};

const navigateToShareability = () => {
    router.push({ name: "sharebility" });
};

const navigateToApprovalsMembers = () => {
    router.push({ name: "approvals-members" });
};

const navigateToDefaultPartners = () => {
    router.push({ name: "default-partners" });
};

const navigateToAIChatEndPoint = async () => {
    if (creatingChat.value) return; // Prevent multiple clicks

    try {
        creatingChat.value = true;

        // Show custom loading
        const loadingDiv = document.createElement("div");
        loadingDiv.id = "custom-loading";
        loadingDiv.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(255, 255, 255, 0.9);
            display: flex;
            align-items: center;
            justify-content: center;
            z-index: 9999;
            color: black;
            font-size: 18px;
            font-family: Arial, sans-serif;        `;
        loadingDiv.innerHTML = "Initializing Chat...";
        document.body.appendChild(loadingDiv);

        console.log("Checking for existing general chats...");

        // First check if user has existing general chats
        const checkResponse = await ChatApi.checkGeneralChats();
        console.log("General chats check response:", checkResponse);

        // Close loading
        const existingLoadingDiv = document.getElementById("custom-loading");
        if (existingLoadingDiv) {
            existingLoadingDiv.remove();
        }

        if (
            checkResponse.data &&
            checkResponse.data.data &&
            checkResponse.data.data.has_chats_with_messages
        ) {
            // User has existing general chats with messages, show choice dialog
            const latestChat =
                checkResponse.data.data.latest_chat_with_messages;
            const hasMessages =
                latestChat &&
                latestChat.messages &&
                latestChat.messages.length > 0;

            if (hasMessages) {
                // Chat has messages, ask if user wants to continue or create new
                const userChoice = await showChatChoiceDialog(latestChat);

                if (userChoice === "continue") {
                    // Continue existing chat
                    console.log("User chose to continue existing chat");
                    const chatId = latestChat.id;
                    creatingChat.value = false;
                    router.push({ name: "chats.view", params: { id: chatId } });
                    return;
                } else if (userChoice === "new") {
                    // Create new chat
                    console.log("User chose to create new chat");
                    const createResponse =
                        await ChatApi.createGeneralChat("new");
                    if (
                        createResponse.data &&
                        createResponse.data.data &&
                        createResponse.data.data.chat
                    ) {
                        const chatId = createResponse.data.data.chat.id;
                        creatingChat.value = false;
                        router.push({
                            name: "chats.view",
                            params: { id: chatId },
                        });
                        return;
                    }
                } else {
                    // User cancelled
                    creatingChat.value = false;
                    return;
                }
            }
        }

        // Check if user has any general chats (including empty ones)
        if (
            checkResponse.data &&
            checkResponse.data.data &&
            checkResponse.data.data.has_chats
        ) {
            // User has general chats, check if the latest is empty
            const latestChat = checkResponse.data.data.latest_chat;
            const hasMessages =
                latestChat &&
                latestChat.messages &&
                latestChat.messages.length > 0;

            if (!hasMessages) {
                // Latest chat is empty, just open it directly
                console.log("Opening existing empty chat directly");
                const chatId = latestChat.id;
                creatingChat.value = false;
                router.push({ name: "chats.view", params: { id: chatId } });
                return;
            }
        }

        // No existing chats or all chats have messages but user chose not to continue, create new one
        console.log("No existing general chats or creating new one");
        const createResponse = await ChatApi.createGeneralChat("new");
        if (
            createResponse.data &&
            createResponse.data.data &&
            createResponse.data.data.chat
        ) {
            const chatId = createResponse.data.data.chat.id;
            creatingChat.value = false;
            router.push({ name: "chats.view", params: { id: chatId } });
            return;
        }

        creatingChat.value = false;

        // Close loading if still open
        const existingLoadingDiv2 = document.getElementById("custom-loading");
        if (existingLoadingDiv2) {
            existingLoadingDiv2.remove();
        }
    } catch (error) {
        console.error("Failed to handle general chat:", error);
        console.error("Error details:", error.stack);

        // Close loading if still open
        const existingLoadingDiv3 = document.getElementById("custom-loading");
        if (existingLoadingDiv3) {
            existingLoadingDiv3.remove();
        }

        ElNotification({
            title: "Error",
            message: "Failed to create chat. Please try again.",
            type: "error",
            position: "bottom-right",
            duration: 5000,
        });
        creatingChat.value = false;
    }
};

// Show dialog to choose between continuing existing chat or creating new
const showChatChoiceDialog = (latestChat) => {
    return new Promise((resolve) => {
        // Use ElMessageBox directly from Element Plus
        ElMessageBox.confirm(
            `You have an existing general chat from ${formatChatDate(latestChat.created_at)}. What would you like to do?`,
            "Chat Choice",
            {
                confirmButtonText: "Continue Existing",
                cancelButtonText: "Start New",
                type: "info",
                distinguishCancelAndClose: true,
                closeOnClickModal: false,
                closeOnPressEscape: true,
                beforeClose: (action, instance, done) => {
                    if (action === "close") {
                        resolve("cancel");
                        done();
                    } else {
                        done();
                    }
                },
            },
        )
            .then(() => {
                resolve("continue");
            })
            .catch((action) => {
                if (action === "cancel") {
                    resolve("new");
                } else {
                    resolve("cancel");
                }
            });
    });
};

// Format chat date for display
const formatChatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 1) return "today";
    if (diffDays === 2) return "yesterday";
    if (diffDays <= 7) return `${diffDays - 1} days ago`;

    return date.toLocaleDateString();
};

const logout = async () => {
    useUserStore()
        .logout()
        .then(() => {
            localStorage.clear();
            router.push({ name: "login" });
        })
        .catch((error) => {});
};

onMounted(async () => {
    // Load metadata if not already loaded
    if (!metadataStore.loaded) {
        await metadataStore.fetchData();
    }
    getMyRequests();
    getPinnedLists(); // Fetch pinned lists

    Echo.channel("items").listen(".count-event", (event) => {
        getMyRequests();
    });

    // Modals moved to TopBar; events handled there now
});

const showMainSettings = ref(false);
const showLists = ref(false);
const showDocuments = ref(false);
const showUploadDropdown = ref(false);

const onDocumentCreated = (doc) => {
    try {
        window.dispatchEvent(
            new CustomEvent("document-created", { detail: doc }),
        );
    } catch (e) {}
};

const { collapse, handleOpen, handleClose } = defineProps({
    collapse: { type: Boolean, default: false },
    handleOpen: { type: Function, default: () => {} },
    handleClose: { type: Function, default: () => {} },
});

// Local wrappers to avoid Vue instance method resolution warnings
const onMenuOpen = (...args) => {
    try {
        handleOpen && handleOpen(...args);
        // Update popup colors when menu opens
        if (isCollapsed.value) {
            setTimeout(() => updatePopupMenuColors(true), 50);
        }
    } catch (e) {}
};
const onMenuClose = (...args) => {
    try {
        handleClose && handleClose(...args);
    } catch (e) {}
};

// Dialog state moved to TopBar

// Bridge functions to open dialogs via global events
const openUploadDocumentFromSidebar = () => {
    try {
        globalThis.dispatchEvent(
            new CustomEvent("open-upload-document", { detail: {} }),
        );
    } catch (e) {
        try {
            window &&
                window.dispatchEvent(
                    new CustomEvent("open-upload-document", { detail: {} }),
                );
        } catch (_) {}
    }
};

const openCreateProjectFromSidebar = () => {
    try {
        globalThis.dispatchEvent(
            new CustomEvent("open-create-project", { detail: {} }),
        );
    } catch (e) {
        try {
            window &&
                window.dispatchEvent(
                    new CustomEvent("open-create-project", { detail: {} }),
                );
        } catch (_) {}
    }
};

const openCreateListFromSidebar = () => {
    try {
        globalThis.dispatchEvent(
            new CustomEvent("open-create-list", { detail: {} }),
        );
    } catch (e) {
        try {
            window &&
                window.dispatchEvent(
                    new CustomEvent("open-create-list", { detail: {} }),
                );
        } catch (_) {}
    }
};

const isCollapsed = ref(collapse);
const defaultOpenedMenus = ref([]); // Default to closed menu
const activeMenuIndex = ref("1"); // Default active menu

const handleToggle = () => {
    isCollapsed.value = !isCollapsed.value;
    emit("toggle", isCollapsed.value);
};

watch(
    () => collapse,
    (newValue) => {
        isCollapsed.value = newValue;
    },
);

// Watch for sidebar collapse state and update popup menu text colors
watch(
    () => isCollapsed.value,
    (newValue) => {
        updatePopupMenuColors(newValue);
    },
);

const updatePopupMenuColors = (isCollapsed) => {
    // Wait for DOM to update
    setTimeout(() => {
        const popupMenus = document.querySelectorAll(
            ".el-menu--popup, .el-menu--popup-right-start",
        );

        popupMenus.forEach((popup) => {
            if (isCollapsed) {
                popup.style.backgroundColor = "#fff";

                // Target all possible text elements
                const textElements = popup.querySelectorAll(
                    ".el-menu-item, .el-sub-menu__title, .el-menu-item span, .el-sub-menu__title span, .wrap-text-side",
                );

                textElements.forEach((element) => {
                    element.style.color = "#34267d !important";
                });
            } else {
                popup.style.backgroundColor = ""; // Reset to default

                // Reset all text elements
                const textElements = popup.querySelectorAll(
                    ".el-menu-item, .el-sub-menu__title, .el-menu-item span, .el-sub-menu__title span, .wrap-text-side",
                );

                textElements.forEach((element) => {
                    element.style.color = "#34267d"; // Reset to default
                });
            }
        });

        // Additional catch-all for all popup text elements
        const popupTexts = document.querySelectorAll(".el-menu-item *");
        popupTexts.forEach((text) => {
            text.style.color = "#34267d !important";
        });

        // Add hover styles for collapsed popup menus
        if (isCollapsed) {
            // Apply inline styles directly to popup elements
            popupMenus.forEach((popup) => {
                popup.style.backgroundColor = "#fff";

                // Apply styles to all menu items
                const menuItems = popup.querySelectorAll(
                    ".el-menu-item, .el-sub-menu__title",
                );
                menuItems.forEach((item) => {
                    item.style.color = "#34267d";
                    item.style.backgroundColor = "transparent";

                    // Add hover event listener
                    item.addEventListener("mouseenter", () => {
                        item.style.backgroundColor = "rgba(255, 255, 255, 0.1)";
                        item.style.color = "#34267d";
                    });

                    item.addEventListener("mouseleave", () => {
                        item.style.backgroundColor = "transparent";
                        item.style.color = "#34267d";
                    });
                });

                // Apply to all child elements
                const allElements = popup.querySelectorAll("*");
                allElements.forEach((element) => {
                    element.style.color = "#000";
                });
            });
        } else {
            // Remove hover styles when sidebar is expanded
            const existingStyle = document.getElementById(
                "collapsed-popup-hover",
            );
            if (existingStyle) {
                existingStyle.remove();
            }
        }
    }, 100); // Small delay to ensure DOM is updated
};

// Watch for route changes to open appropriate menu
watch(
    () => route.name,
    (newRouteName) => {
        if (newRouteName === "projects" || newRouteName === "projects.view") {
            defaultOpenedMenus.value = ["2"];
            updateProjectsActive();
        } else if (
            newRouteName === "documents" ||
            newRouteName === "documents.category" ||
            newRouteName === "documents.view"
        ) {
            defaultOpenedMenus.value = [];
            if (newRouteName === "documents") {
                activeMenuIndex.value = "3-1"; // All Documents
            } else if (route.params.slug === "all") {
                activeMenuIndex.value = "3-1"; // All Documents
            } else if (route.params.slug === "tam-projects") {
                activeMenuIndex.value = "3-2"; // TAM Projects Documents
            } else if (route.params.slug === "domain-offering") {
                activeMenuIndex.value = "3-3"; // Domain/Offering Documents
            } else if (route.params.slug === "support-service") {
                activeMenuIndex.value = "3-4"; // Support Service Documents
            } else if (route.params.slug === "third-party") {
                activeMenuIndex.value = "3-5"; // Third Party Documents
            } else if (route.params.slug === "other") {
                activeMenuIndex.value = "3-6"; // Other Documents
            } else {
                activeMenuIndex.value = "3-1"; // Default to All Documents
            }
        } else if (
            newRouteName === "projects-requests" ||
            newRouteName === "documents-requests"
        ) {
            defaultOpenedMenus.value = ["4"];
            activeMenuIndex.value =
                newRouteName === "projects-requests" ? "4-1" : "4-2";
        } else if (newRouteName === "my.knowledge") {
            defaultOpenedMenus.value = ["5"];
            activeMenuIndex.value = "5";
        } else if (newRouteName === "analytics") {
            defaultOpenedMenus.value = ["6"];
            activeMenuIndex.value = "6";
        } else if (newRouteName === "tamers") {
            defaultOpenedMenus.value = ["7"];
            activeMenuIndex.value = "7-1";
        } else if (newRouteName === "lists" || newRouteName === "lists.view") {
            updateListsActive();
        } else if (
            newRouteName === "settings" ||
            newRouteName === "users" ||
            newRouteName === "roles" ||
            newRouteName === "clients" ||
            newRouteName === "clients-types" ||
            newRouteName === "service-lines" ||
            newRouteName === "business-domains" ||
            newRouteName === "business-units" ||
            newRouteName === "offerings" ||
            newRouteName === "industry" ||
            newRouteName === "digital-solutions" ||
            newRouteName === "managed-services" ||
            newRouteName === "document-types" ||
            newRouteName === "document-types-mapping" ||
            newRouteName === "sharebility" ||
            newRouteName === "approvals-members" ||
            newRouteName === "default-partners"
        ) {
            defaultOpenedMenus.value = ["9"];
            if (newRouteName === "users") activeMenuIndex.value = "9-1";
            else if (newRouteName === "roles") activeMenuIndex.value = "9-2";
            else if (newRouteName === "clients") activeMenuIndex.value = "9-3";
            else if (newRouteName === "clients-types")
                activeMenuIndex.value = "9-4";
            else if (newRouteName === "service-lines")
                activeMenuIndex.value = "9-5";
            else if (newRouteName === "business-domains")
                activeMenuIndex.value = "9-6";
            else if (newRouteName === "business-units")
                activeMenuIndex.value = "9-7";
            else if (newRouteName === "offerings")
                activeMenuIndex.value = "9-8";
            else if (newRouteName === "industry") activeMenuIndex.value = "9-9";
            else if (newRouteName === "digital-solutions")
                activeMenuIndex.value = "9-10";
            else if (newRouteName === "managed-services")
                activeMenuIndex.value = "9-11";
            else if (newRouteName === "document-types")
                activeMenuIndex.value = "9-12";
            else if (newRouteName === "sharebility")
                activeMenuIndex.value = "9-13";
            else if (newRouteName === "approvals-members")
                activeMenuIndex.value = "9-14";
            else if (newRouteName === "default-partners")
                activeMenuIndex.value = "9-15";
            else if (newRouteName === "document-types-mapping")
                activeMenuIndex.value = "9-16";
            else activeMenuIndex.value = "9-1";
        } else if (newRouteName === "faq") {
            defaultOpenedMenus.value = ["10"];
            activeMenuIndex.value = "10";
        } else if (newRouteName === "chats.view") {
            defaultOpenedMenus.value = [];
            activeMenuIndex.value = "11";
        } else {
            defaultOpenedMenus.value = [];
            activeMenuIndex.value = "1";
        }
    },
    { immediate: true },
);

const isBusinessDomainActive = (domainIndex) => {
    // Check if the active menu index matches this domain's index
    // This handles both when domain is directly selected (2-1) or when a child offering is selected (2-1-1)
    const domainSubmenuIndex = `2-${domainIndex + 1}`;
    return (
        activeMenuIndex.value === domainSubmenuIndex ||
        activeMenuIndex.value.startsWith(`${domainSubmenuIndex}-`)
    );
};

const isListCategoryActive = (category) => {
    // category can be 'my_list' or 'shared_with_me'
    const categoryIndex = category === "my_list" ? "8-1" : "8-2";
    return (
        activeMenuIndex.value === categoryIndex ||
        activeMenuIndex.value.startsWith(`${categoryIndex}-`)
    );
};

function updateProjectsActive() {
    //     const raw = route.query["OfferingId[0]"];
    //     if (raw) {
    //         const offeringId = parseInt(raw);
    //         const offeringIndex = offerings.value.findIndex(
    //             (o) => o.id === offeringId
    //         );
    //         if (offeringIndex !== -1) {
    //             activeMenuIndex.value = `2-${offeringIndex + 2}`;
    //             return;
    //         }
    //     }
    //     activeMenuIndex.value = "";
    // }

    // watch([() => offerings.value, () => route.query["OfferingId[0]"]], () => {
    //     if (route.name === "projects" || route.name === "projects.view") {
    //         updateProjectsActive();
    //     }
    // });

    // Always start with just the Projects submenu opened
    const openedMenus = ["2"];

    // Check for BusinessDomainId first
    const businessDomainRaw = route.query["BusinessDomainId[0]"];
    if (businessDomainRaw) {
        const businessDomainId = parseInt(businessDomainRaw);
        // Find the business domain in the grouped structure
        for (
            let domainIndex = 0;
            domainIndex < offeringsByBusinessDomain.value.length;
            domainIndex++
        ) {
            const domainGroup = offeringsByBusinessDomain.value[domainIndex];
            if (domainGroup.domain.id === businessDomainId) {
                const domainSubmenuIndex = `2-${domainIndex + 1}`;
                // Check if there's also an OfferingId filter
                const offeringRaw = route.query["OfferingId[0]"];
                if (offeringRaw) {
                    const offeringId = parseInt(offeringRaw);
                    const offeringIndex = domainGroup.offerings.findIndex(
                        (o) => o.id === offeringId,
                    );
                    if (offeringIndex !== -1) {
                        activeMenuIndex.value = `${domainSubmenuIndex}-${offeringIndex + 1}`;
                        openedMenus.push(domainSubmenuIndex);
                        defaultOpenedMenus.value = openedMenus;
                        return;
                    }
                } else {
                    // Only business domain is selected, highlight the domain submenu
                    activeMenuIndex.value = domainSubmenuIndex;
                    openedMenus.push(domainSubmenuIndex);
                    defaultOpenedMenus.value = openedMenus;
                    return;
                }
            }
        }
    }

    // Check for OfferingId only (without BusinessDomainId)
    const offeringRaw = route.query["OfferingId[0]"];
    if (offeringRaw) {
        const offeringId = parseInt(offeringRaw);
        // Find the offering in the grouped structure
        for (
            let domainIndex = 0;
            domainIndex < offeringsByBusinessDomain.value.length;
            domainIndex++
        ) {
            const domainGroup = offeringsByBusinessDomain.value[domainIndex];
            const offeringIndex = domainGroup.offerings.findIndex(
                (o) => o.id === offeringId,
            );
            if (offeringIndex !== -1) {
                const domainSubmenuIndex = `2-${domainIndex + 1}`;
                activeMenuIndex.value = `${domainSubmenuIndex}-${offeringIndex + 1}`;
                openedMenus.push(domainSubmenuIndex);
                defaultOpenedMenus.value = openedMenus;
                return;
            }
        }
    }

    // No specific filter, just keep Projects submenu open
    activeMenuIndex.value = "";
    defaultOpenedMenus.value = openedMenus;
}

watch(
    [
        () => offeringsByBusinessDomain.value,
        () => route.query["OfferingId[0]"],
        () => route.query["BusinessDomainId[0]"],
    ],
    () => {
        if (route.name === "projects") {
            updateProjectsActive();
        }
    },
);

watch(
    [
        () => myPinnedLists.value,
        () => sharedPinnedLists.value,
        () => route.query.ListType,
        () => route.params.id,
    ],
    () => {
        if (route.name === "lists" || route.name === "lists.view") {
            updateListsActive();
        }
    },
);

onMounted(() => {
    // Add click listener for mobile sidebar
    //document.addEventListener('click', handleOutsideClick);

    // Initialize popup colors
    updatePopupMenuColors(isCollapsed.value);
});

onUnmounted(() => {
    // Remove click listener and body class
    //document.removeEventListener('click', handleOutsideClick);
    //document.body.classList.remove('sidebar-open');
});
</script>

<template>
    <div
        class="new-side-bar h-100 d-flex flex-column position-fixed"
        style="background-color: var(--main-color); width: inherit"
    >
        <!-- <img
      src="../../../assets/img/back-vector.png"
      alt="side-bar-bg"
      class="side-bar-bg"
    /> -->
        <div
            class="sidebar--header d-flex justify-content-between align-items-center"
        >
            <div class="sidebar-brand-icon">
                <Logo v-if="!isCollapsed" />
                <LogoMini v-if="isCollapsed" />
            </div>
            <div class="toggle-btn" @click="handleToggle">
                <Collapse class="nav-link-text" v-if="isCollapsed" />
                <UnCollapse class="nav-link-text" v-if="!isCollapsed" />
            </div>
        </div>

        <el-menu
            :default-active="activeMenuIndex"
            class="el-menu-vertical-demo"
            :collapse="isCollapsed"
            :default-openeds="defaultOpenedMenus"
            :unique-opened="true"
            @open="onMenuOpen"
            @close="onMenuClose"
        >
            <!-- Home -->
            <el-menu-item index="1" @click="navigateToHome">
                <Home />
                <span>Home</span>
            </el-menu-item>
            <!-- Projects Dropdown -->
            <el-sub-menu
                index="2"
                :class="
                    route.name === 'projects' || route.name === 'projects.view'
                        ? 'is-active'
                        : ''
                "
            >
                <template #title>
                    <ProjectsMenu @click.stop="navigateToAllProjects" />
                    <span @click.stop="navigateToAllProjects">Projects</span>
                </template>
                <!-- <el-menu-item
                    index="2-1"
                    @click="navigateToAllProjects"
                >
                    All Projects
                </el-menu-item> -->
                <el-sub-menu
                    v-for="(
                        domainGroup, domainIndex
                    ) in offeringsByBusinessDomain"
                    :key="domainGroup.domain.id"
                    :index="`2-${domainIndex + 1}`"
                    :class="[
                        'business-domain-submenu',
                        isBusinessDomainActive(domainIndex) ? 'is-active' : '',
                    ]"
                >
                    <template #title>
                        <span
                            class="truncate-text-side"
                            @click.stop="
                                navigateToProjectByBusinessDomain(
                                    domainGroup.domain,
                                )
                            "
                        >
                            {{ domainGroup.domain.name }}
                        </span>
                    </template>
                    <el-menu-item
                        v-for="(
                            offering, offeringIndex
                        ) in domainGroup.offerings"
                        :key="offering.id"
                        :index="`2-${domainIndex + 1}-${offeringIndex + 1}`"
                        @click="navigateToProjectByOffering(offering)"
                    >
                        <span class="truncate-text-side">{{
                            offering.name
                        }}</span>
                    </el-menu-item>
                </el-sub-menu>
            </el-sub-menu>
            <!-- Documents Dropdown -->
            <el-sub-menu
                index="3"
                :class="
                    route.name === 'documents' ||
                    route.name === 'documents.category' ||
                    route.name === 'documents.view'
                        ? 'is-active'
                        : ''
                "
            >
                <template #title>
                    <DocumentsMenu @click.stop="navigateToAllDocuments" />
                    <span @click.stop="navigateToAllDocuments">Documents</span>
                </template>
                <!-- <el-menu-item
                    index="3-1"
                    @click="navigateToAllDocuments"
                >
                    All Documents
                </el-menu-item> -->
                <el-menu-item index="3-2" @click="navigateToTamProjects">
                    <span class="truncate-text-side"
                        >TAM Projects Documents</span
                    >
                </el-menu-item>
                <el-menu-item index="3-3" @click="navigateToDomainOffering">
                    <span class="truncate-text-side"
                        >Domain/Offering Documents</span
                    >
                </el-menu-item>
                <el-menu-item index="3-4" @click="navigateToSupportService">
                    <span class="truncate-text-side"
                        >Support Service Documents</span
                    >
                </el-menu-item>
                <el-menu-item index="3-5" @click="navigateToThirdParty">
                    <span class="truncate-text-side"
                        >Third Party Documents</span
                    >
                </el-menu-item>
                <el-menu-item index="3-6" @click="navigateToOtherDocuments">
                    <span class="truncate-text-side">Other Documents</span>
                </el-menu-item>
            </el-sub-menu>
            <!-- Requests Dropdown -->
            <el-sub-menu index="4">
                <template #title>
                    <RequestsMenu />
                    <span>Requests</span>
                </template>
                <el-menu-item
                    index="4-1"
                    @click="navigateToRequests('projects')"
                    >Projects Requests</el-menu-item
                >
                <el-menu-item
                    index="4-2"
                    @click="navigateToRequests('documents')"
                    >Documents Requests</el-menu-item
                >
            </el-sub-menu>
            <!-- My Activities -->
            <el-menu-item index="5" @click="navigateToMyActivities">
                <MyActivitiesMenu />
                <span>My Activities</span>
            </el-menu-item>
            <!-- Analytics -->
            <el-menu-item index="6" @click="navigateToAnalytics">
                <AnalyticsMenu />
                <span>Analytics</span>
            </el-menu-item>
            <!-- Tamers Dropdown -->
            <el-sub-menu index="7">
                <template #title>
                    <TamersMenu />
                    <span>Tamers</span>
                </template>
                <el-menu-item index="7-1" @click="navigateToTamers"
                    >Tamers domain (1)</el-menu-item
                >
                <el-menu-item index="7-2" @click="navigateToTamers"
                    >Tamers domain (2)</el-menu-item
                >
                <el-menu-item index="7-3" @click="navigateToTamers"
                    >Tamers domain (3)</el-menu-item
                >
            </el-sub-menu>
            <!-- Lists Dropdown -->
            <el-sub-menu
                index="8"
                :class="
                    route.name === 'lists' || route.name === 'lists.view'
                        ? 'is-active'
                        : ''
                "
            >
                <template #title>
                    <ListsMenu
                        @click.stop="
                            navigateToLists(['my_list', 'shared_with_me'])
                        "
                    />
                    <span
                        @click.stop="
                            navigateToLists(['my_list', 'shared_with_me'])
                        "
                        >Lists</span
                    >
                </template>

                <!-- My Lists Sub-menu -->
                <el-sub-menu
                    index="8-1"
                    :class="[
                        'list-category-submenu',
                        isListCategoryActive('my_list') ? 'is-active' : '',
                    ]"
                >
                    <template #title>
                        <span @click.stop="navigateToLists('my_list')"
                            >My Lists</span
                        >
                    </template>
                    <el-menu-item
                        v-for="(list, index) in myPinnedLists"
                        :key="list.id"
                        :index="`8-1-${index + 1}`"
                        @click="navigateToList(list.id)"
                    >
                        <span class="truncate-text-side">{{ list.name }}</span>
                    </el-menu-item>
                </el-sub-menu>

                <!-- Shared Lists Sub-menu -->
                <el-sub-menu
                    index="8-2"
                    :class="[
                        'list-category-submenu',
                        isListCategoryActive('shared_with_me')
                            ? 'is-active'
                            : '',
                    ]"
                >
                    <template #title>
                        <span @click.stop="navigateToLists('shared_with_me')"
                            >Shared With Me</span
                        >
                    </template>
                    <el-menu-item
                        v-for="(list, index) in sharedPinnedLists"
                        :key="list.id"
                        :index="`8-2-${index + 1}`"
                        @click="navigateToList(list.id)"
                    >
                        <span class="truncate-text-side">{{ list.name }}</span>
                    </el-menu-item>
                </el-sub-menu>
            </el-sub-menu>
            <!-- Settings Dropdown -->
            <el-sub-menu
                index="9"
                v-if="
                    UserService.hasPermission('read', 'Role') ||
                    UserService.hasPermission('read', 'User') ||
                    UserService.hasPermission('read', 'Client') ||
                    UserService.hasPermission('read', 'ClientType') ||
                    UserService.hasPermission('read', 'ServiceLine') ||
                    UserService.hasPermission('read', 'BusinessDomain') ||
                    UserService.hasPermission('read', 'BusinessUnit') ||
                    UserService.hasPermission('read', 'Offering') ||
                    UserService.hasPermission('read', 'Industry') ||
                    UserService.hasPermission('read', 'DigitalSolution') ||
                    UserService.hasPermission('read', 'ManagedService') ||
                    UserService.hasPermission('read', 'DocumentType') ||
                    UserService.hasPermission('read', 'Shareability')
                "
            >
                <template #title>
                    <SettingsMenu />
                    <span>Settings</span>
                </template>
                <el-menu-item
                    index="9-1"
                    @click="navigateToUsers"
                    v-if="UserService.hasPermission('read', 'User')"
                >
                    Users
                </el-menu-item>
                <el-menu-item
                    index="9-2"
                    @click="navigateToRoles"
                    v-if="UserService.hasPermission('read', 'Role')"
                >
                    Designation
                </el-menu-item>
                <el-menu-item
                    index="9-3"
                    @click="navigateToClients"
                    v-if="UserService.hasPermission('read', 'Client')"
                >
                    Clients
                </el-menu-item>
                <el-menu-item
                    index="9-4"
                    @click="navigateToClientTypes"
                    v-if="UserService.hasPermission('read', 'ClientType')"
                >
                    Client Types
                </el-menu-item>
                <el-menu-item
                    index="9-5"
                    @click="navigateToServiceLines"
                    v-if="UserService.hasPermission('read', 'ServiceLine')"
                >
                    Service Lines
                </el-menu-item>
                <el-menu-item
                    index="9-6"
                    @click="navigateToBusinessDomains"
                    v-if="UserService.hasPermission('read', 'BusinessDomain')"
                >
                    Business Domains
                </el-menu-item>
                <!-- <el-menu-item
                    index="9-7"
                    @click="navigateToBusinessUnits"
                    v-if="UserService.hasPermission('read', 'BusinessUnit')"
                >
                    Business Units
                </el-menu-item> -->
                <el-menu-item
                    index="9-8"
                    @click="navigateToOfferings"
                    v-if="UserService.hasPermission('read', 'Offering')"
                >
                    Offerings
                </el-menu-item>
                <el-menu-item
                    index="9-9"
                    @click="navigateToIndustry"
                    v-if="UserService.hasPermission('read', 'Industry')"
                >
                    Industry
                </el-menu-item>
                <el-menu-item
                    index="9-10"
                    @click="navigateToDigitalSolutions"
                    v-if="UserService.hasPermission('read', 'DigitalSolution')"
                >
                    Digital Solution
                </el-menu-item>
                <el-menu-item
                    index="9-11"
                    @click="navigateToManagedServices"
                    v-if="UserService.hasPermission('read', 'ManagedService')"
                >
                    Managed Services
                </el-menu-item>
                <el-menu-item
                    index="9-12"
                    @click="navigateToDocumentTypes"
                    v-if="UserService.hasPermission('read', 'DocumentType')"
                >
                    Document Types
                </el-menu-item>
                <el-menu-item
                    index="9-16"
                    @click="navigateToDocumentTypesMapping"
                    v-if="UserService.hasPermission('read', 'DocumentType')"
                >
                    Document Types Mapping
                </el-menu-item>
                <el-menu-item
                    index="9-13"
                    @click="navigateToShareability"
                    v-if="UserService.hasPermission('read', 'Shareability')"
                >
                    Sharebility
                </el-menu-item>
                <el-menu-item
                    index="9-14"
                    @click="navigateToApprovalsMembers"
                    v-if="
                        UserService.hasPermission(
                            'manage-project-approvals',
                            'Setting',
                        )
                    "
                >
                    Approvals Members
                </el-menu-item>
                <el-menu-item
                    index="9-15"
                    @click="navigateToDefaultPartners"
                    v-if="UserService.hasPermission('read', 'DefaultPartner')"
                >
                    Default Partners
                </el-menu-item>
            </el-sub-menu>
            <!-- FAQ -->
            <el-menu-item index="10" @click="navigateToFAQ">
                <FAQMenu />
                <span>FAQ</span>
            </el-menu-item>

            <!-- AI  -->
            <el-menu-item
                index="11"
                @click="navigateToAIChatEndPoint"
                :disabled="creatingChat"
                :class="{ loading: creatingChat }"
            >
                <img src="/img/ai-star.png" alt="AI Icon" style="width: 25px;" />
                <span style="font-style: italic;">
                    {{ creatingChat ? "Creating Chat..." : "Chat with Osharek !" }}
                </span>

            </el-menu-item>

            <div class="sidebar-footer w-100 m-0 p-0">
                <div class="btn-group dropup uploadBtn mainDropdown">
                    <el-popover
                        class="box-item"
                        placement="top-start"
                        trigger="click"
                        popper-class="new-add-popover"
                    >
                        <template #reference>
                            <span
                                class="btn btn-secondary upload--btn mw-145"
                                type="span"
                            >
                                <UploadMenu />
                                <span class="upload--text">Upload New</span>
                            </span>
                        </template>
                        <div class="d-flex flex-column gap-2">
                            <a
                                class="dropdown-item"
                                href="#"
                                @click.prevent="openUploadDocumentFromSidebar()"
                            >
                                Upload Documents
                            </a>
                            <a
                                class="dropdown-item"
                                href="#"
                                @click.prevent="openCreateProjectFromSidebar()"
                            >
                                Create New Project
                            </a>
                        </div>
                    </el-popover>
                </div>

                <!-- Create Lists Button -->
                <div class="btn-group dropup uploadBtn mainDropdown mt-0">
                    <span
                        class="btn btn-primary create-lists--btn list-button-create mw-145"
                        @click.prevent="openCreateListFromSidebar()"
                    >
                        <ListsMenu class="list-icon-create" />
                        <span class="upload--text">Create Lists</span>
                    </span>
                </div>
            </div>
        </el-menu>
    </div>
    <!-- Element Plus dialogs to match View.vue behavior -->
    <!-- Modals moved to TopBar -->
</template>

<style scoped>
.new-side-bar {
    min-height: 100vh;
    transition: all 0.3s ease;
    top: 0;
    left: 0;
    bottom: 0;
    z-index: 1030;
    /* scrollbar-color: #FDB236 transparent;  */
}

::-webkit-scrollbar-thumb {
    background-color: #fdb236;
    border: 1px solid #f0f0f0;
}

::-webkit-scrollbar-thumb:horizontal {
    border-radius: 10px 10px 0 0;
    max-height: 2px;
}

::-webkit-scrollbar-thumb:vertical {
    border-radius: 10px 0 0 10px;
}

/* .sidebar-brand-icon {
    padding: 1rem;
    display: flex;
    justify-content: center;
    align-items: center;
} */

.toggle-btn {
    padding: 0.75rem;
    cursor: pointer;
    display: flex;
    justify-content: flex-end;
    border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.el-menu-vertical-demo {
    flex: 1;
    width: 100%;
    border-right: none;
}

/* .el-menu--popup-right-start {
    background-color: #34267d !important;
} */
:deep(.el-menu) {
    border-right: none;
    background-color: transparent !important;
}

:deep(.el-menu-item),
:deep(.el-sub-menu__title) {
    color: rgba(255, 255, 255, 0.8) !important;
    /* color: #000 !important; */
    overflow: hidden;
}

/* Allow menu items with wrapping text to overflow properly */
:deep(.el-menu-item:has(.wrap-text-side)) {
    overflow: visible !important;
    white-space: normal !important;
    align-items: flex-start !important;
    padding: 10px 32px !important;
    min-height: auto !important;
    height: auto !important;
}

/* Fallback for browsers without :has() support */
:deep(.el-menu-item .wrap-text-side) {
    overflow: visible !important;
    white-space: normal !important;
}

/* ul.el-menu--popup {
    background-color: #34267d !important;
}

:deep(.el-menu--popup) {
    background-color: #34267d !important;
}

:deep(.el-menu--popup-right-start) {
    background-color: #34267d !important;
} */

:deep(.el-menu-item:hover),
:deep(.el-sub-menu__title:hover) {
    color: white !important;
    background-color: rgba(255, 255, 255, 0.1) !important;
}

/* Ensure hover state also respects wrapping text */
:deep(.el-menu-item:hover:has(.wrap-text-side)) {
    overflow: visible !important;
    white-space: normal !important;
    align-items: flex-start !important;
}

:deep(.el-menu-item.is-active) {
    color: #fdb236 !important;
    font-weight: 700 !important;
    background-color: rgba(255, 255, 255, 0.2) !important;

    svg path {
        stroke: #fdb236 !important;
        fill: #fdb236 !important;
    }

    .el-icon.el-sub-menu__icon-arrow {
        svg {
            stroke: #fdb236 !important;
            color: #fdb236;
        }
    }
}

:deep(.el-sub-menu.is-active > .el-sub-menu__title) {
    color: #fdb236 !important;
    font-weight: 700 !important;

    svg {
        stroke: #fdb236 !important;
        color: #fdb236;
    }

    .el-sub-menu__icon-arrow {
        color: #fdb236 !important;
    }
}

/* Ensure business domain submenus only look active when truly active,
   and are not all highlighted just because the parent Projects submenu is opened.
   We must override the global `.new-side-bar .el-sub-menu.is-opened .el-sub-menu__title` rule. */
.new-side-bar:deep(
    .el-sub-menu.is-opened .business-domain-submenu .el-sub-menu__title
),
.new-side-bar:deep(
    .el-sub-menu.is-opened .list-category-submenu .el-sub-menu__title
) {
    color: rgba(255, 255, 255, 0.8) !important;
    font-weight: 400 !important;
}

.new-side-bar:deep(
    .el-sub-menu.is-opened
        .business-domain-submenu.is-active
        > .el-sub-menu__title
),
.new-side-bar:deep(
    .el-sub-menu.is-opened
        .list-category-submenu.is-active
        > .el-sub-menu__title
) {
    color: #fdb236 !important;
    font-weight: 700 !important;
}

.new-side-bar:deep(
    .el-sub-menu.is-opened
        .business-domain-submenu.is-active
        > .el-sub-menu__title
        .el-sub-menu__icon-arrow
),
.new-side-bar:deep(
    .el-sub-menu.is-opened
        .list-category-submenu.is-active
        > .el-sub-menu__title
        .el-sub-menu__icon-arrow
) {
    color: #fdb236 !important;
}

.truncate-text-side.wrap-text-side {
    white-space: normal !important;
    word-break: break-word !important;
    text-overflow: unset !important;
    display: block !important;
    width: 100% !important;
    line-height: 1.5 !important;
    padding: 0 !important;
    margin: 0 !important;
    /* color: #000;   */
}

/* Sidebar Footer Styles */
.sidebar-footer {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 1rem !important;
    /* background: rgba(0, 0, 0, 0.2);
    border-top: 1px solid rgba(255, 255, 255, 0.1); */
}

/* Upload Button Styles */
.uploadBtn {
    width: 100%;
    justify-content: center;
    align-items: center;
}

.upload--btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: #6c757d;
    border: 1px solid rgba(255, 255, 255, 0.2);
    border-radius: 0.375rem;
    color: white;
    font-weight: 500;
    transition: all 0.2s ease;
    cursor: pointer;
}

.upload--btn:hover {
    background: #5a6268;
    border-color: rgba(255, 255, 255, 0.3);
    transform: translateY(-1px);
}

.upload--text {
    font-size: 0.875rem;
}

/* Create Lists Button Styles */
.createListsBtn {
    width: 100%;
}

.create-lists--btn {
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
    padding: 0.75rem 1rem;
    background: #007bff;
    border: 1px solid #007bff;
    border-radius: 0.375rem;
    color: white;
    font-weight: 500;
    transition: all 0.2s ease;
    cursor: pointer;
}

.create-lists--btn:hover {
    background: #0056b3;
    border-color: #0056b3;
    transform: translateY(-1px);
}

.create-lists--text {
    font-size: 0.875rem;
}

/* Ensure the span inside menu items wraps properly */
:deep(.el-menu-item .wrap-text-side) {
    overflow: visible !important;
    white-space: normal !important;
    word-break: break-word !important;
    text-overflow: unset !important;
    display: block !important;
    width: 100% !important;
    line-height: 1.5 !important;
    max-width: 100% !important;
    padding: 0 !important;
    margin: 0 !important;
    color: rgba(255, 255, 255, 0.8) !important;
}

.list-button-create {
    margin-top: 0px !important;
    background: #efebff !important;
    color: var(--Colors-Primary-700, #3625a7) !important;
    font-weight: bold !important;
}

.list-icon-create {
    color: var(--Colors-Primary-700, #3625a7) !important;
    fill: var(--Colors-Primary-700, #3625a7) !important;
}

.list-icon-create svg {
    stroke: var(--Colors-Primary-700, #3625a7) !important;
    fill: var(--Colors-Primary-700, #3625a7) !important;
}

.list-icon-create svg * {
    stroke: var(--Colors-Primary-700, #3625a7) !important;
    fill: var(--Colors-Primary-700, #3625a7) !important;
}

.list-icon-create svg path {
    stroke: var(--Colors-Primary-700, #3625a7) !important;
    fill: var(--Colors-Primary-700, #3625a7) !important;
}

/* Deep selectors to penetrate component styles */
:deep(.list-icon-create) {
    color: var(--Colors-Primary-700, #3625a7) !important;
    fill: var(--Colors-Primary-700, #3625a7) !important;
}

:deep(.list-icon-create svg) {
    stroke: var(--Colors-Primary-700, #3625a7) !important;
    fill: var(--Colors-Primary-700, #3625a7) !important;
}

:deep(.list-icon-create svg *) {
    stroke: var(--Colors-Primary-700, #3625a7) !important;
    fill: var(--Colors-Primary-700, #3625a7) !important;
}

:deep(.list-icon-create svg path) {
    stroke: var(--Colors-Primary-700, #3625a7) !important;
    fill: var(--Colors-Primary-700, #3625a7) !important;
}

/* Global override as last resort */

.list-button-create svg {
    stroke: var(--Colors-Primary-700, #3625a7) !important;
    fill: var(--Colors-Primary-700, #3625a7) !important;
}

/* Loading state for AI Chat menu item */
.el-menu-item.loading {
    opacity: 0.7;
    cursor: not-allowed;
}

.el-menu-item.loading span {
    position: relative;
}

.el-menu-item.loading span::after {
    content: "";
    position: absolute;
    width: 12px;
    height: 12px;
    margin: 0 0 0 8px;
    border: 2px solid transparent;
    border-top-color: currentColor;
    border-radius: 50%;
    animation: spin 1s linear infinite;
}

@keyframes spin {
    0% {
        transform: rotate(0deg);
    }

    100% {
        transform: rotate(360deg);
    }
}

.mw-145 {
    max-width: 145px;
    min-width: 55px;
}
</style>
